import {useEffect, useRef} from 'react';
import {useSearchParams} from 'react-router';
import {useReportStore} from './useReportStore';

/**
 * Helper to safely parse JSON array from URL param
 */
function parseJsonArray(value, fallback = []) {
    if (!value) return fallback;
    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : fallback;
    } catch {
        return fallback;
    }
}

/**
 * Hook to initialize Zustand URL params state from search params.
 * This runs only once on mount and does NOT subscribe to search param changes.
 * After initialization, all state is managed through Zustand.
 */
export function useInitUrlParams() {
    const [searchParams] = useSearchParams();
    const urlParamsInitialized = useReportStore((s) => s.urlParamsInitialized);
    const initializeUrlParams = useReportStore((s) => s.initializeUrlParams);

    // Use ref to capture initial searchParams and avoid re-reads
    const initialParamsRef = useRef(null);
    if (initialParamsRef.current === null) {
        initialParamsRef.current = searchParams;
    }

    useEffect(() => {
        // Only initialize once
        if (urlParamsInitialized) return;

        const sp = initialParamsRef.current;

        // Parse search params
        const query = sp.get('query') || '';
        const aiSearch = sp.has('aiSearch');
        const showFilters = sp.has('showFilters');
        const keywords = sp.get('keywords') || '';

        // Parse filter params
        const sourceScoreMinRaw = sp.get('sourceScoreMin');
        const sourceScoreMaxRaw = sp.get('sourceScoreMax');
        const sourceScoreMin = sourceScoreMinRaw != null ? parseFloat(sourceScoreMinRaw) : null;
        const sourceScoreMax = sourceScoreMaxRaw != null ? parseFloat(sourceScoreMaxRaw) : null;
        const docSource = sp.get('docSource') || '';
        const docSourceType = sp.get('docSourceType') || '';
        const tags = parseJsonArray(sp.get('tags'));
        const classificationLevels = parseJsonArray(sp.get('classificationLevels'));

        // Parse section params
        const selectedSectionId = sp.get('selectedSectionId') || null;

        initializeUrlParams({
            query,
            aiSearch,
            showFilters,
            keywords,
            sourceScoreMin: Number.isFinite(sourceScoreMin) ? sourceScoreMin : null,
            sourceScoreMax: Number.isFinite(sourceScoreMax) ? sourceScoreMax : null,
            docSource,
            docSourceType,
            tags,
            classificationLevels,
            selectedSectionId,
        });
    }, [urlParamsInitialized, initializeUrlParams]);

    return urlParamsInitialized;
}
